import Component from "../../lib/Component.js";
import { getStoredConsent, hideConsentBanner, showConsentBanner } from "./ConsentBanner.js";

const ADSENSE_PROBE_URL = "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js";
const CONSENT_WAIT_MS = 3000;

export const applyFallbackConsent = (granted) => {
  const state = granted ? "granted" : "denied";
  if (window.gtag) {
    window.gtag("consent", "update", {
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      analytics_storage: state,
      functionality_storage: state,
      personalization_storage: state,
    });
  }
  (window.dataLayer = window.dataLayer || []).push({ event: "consent_fallback", consent_granted: granted });
};

// Backup consent used when Google's CMP never shows up (e.g. no ads served):
// reuses the stored choice or asks via a subtle bottom banner. Ads stay denied.
export const requestFallbackConsent = () =>
  new Promise((resolve) => {
    const finish = (granted) => {
      applyFallbackConsent(granted);
      resolve({ analytics: granted, ads: false, reason: "cmp-fallback" });
    };
    const stored = getStoredConsent();
    if (stored) {
      finish(stored === "granted");
    } else {
      showConsentBanner(finish);
    }
  });

// A rejected no-cors fetch means the ad network is blocked by the client.
const isAdNetworkBlocked = () =>
  fetch(ADSENSE_PROBE_URL, { mode: "no-cors", cache: "no-store" }).then(
    () => false,
    () => true
  );

const CMP = ({ onConsent }) => {
  // Make sure that the properties exist on the window.
  window.googlefc = window.googlefc || {};
  window.googlefc.callbackQueue = window.googlefc.callbackQueue || [];

  const isGranted = (status) => {
    const {
      CONSENT_MODE_PURPOSE_STATUS_GRANTED,
      CONSENT_MODE_PURPOSE_STATUS_NOT_APPLICABLE,
      CONSENT_MODE_PURPOSE_STATUS_NOT_CONFIGURED,
    } = window.googlefc.ConsentModePurposeStatusEnum;
    return [
      CONSENT_MODE_PURPOSE_STATUS_GRANTED,
      CONSENT_MODE_PURPOSE_STATUS_NOT_APPLICABLE,
      CONSENT_MODE_PURPOSE_STATUS_NOT_CONFIGURED,
    ].includes(status);
  };

  return new Component({
    onMount: () => {
      let resolved = false;
      const resolveOnce = (data) => {
        if (resolved) return;
        resolved = true;
        hideConsentBanner();
        if (onConsent) onConsent(data);
      };

      window.googlefc.callbackQueue.push({
        CONSENT_MODE_DATA_READY: () => {
          const consentValues = window.googlefc.getGoogleConsentModeValues();
          const analytics = isGranted(consentValues.analyticsStoragePurposeConsentStatus);
          const ads =
            isGranted(consentValues.adStoragePurposeConsentStatus) &&
            isGranted(consentValues.adUserDataPurposeConsentStatus) &&
            isGranted(consentValues.adPersonalizationPurposeConsentStatus);

          // Update Google Consent Mode with user's choice
          if (window.gtag) {
            window.gtag("consent", "update", {
              ad_storage: ads ? "granted" : "denied",
              ad_user_data: ads ? "granted" : "denied",
              ad_personalization: ads ? "granted" : "denied",
              analytics_storage: analytics ? "granted" : "denied",
              functionality_storage: analytics ? "granted" : "denied",
              personalization_storage: analytics ? "granted" : "denied",
            });
          }

          resolveOnce({ analytics, ads, reason: "consent-ready" });
        },
      });

      // Skip in development mode; withDevModeFailover is used there.
      if (process.env.NODE_ENV === "development") {
        return;
      }

      // Google's CMP only initializes when an ad is served. If it never reports
      // consent data, tell a real ad blocker apart from "no ads served" and
      // fall back so analytics can still load.
      setTimeout(async () => {
        if (resolved) return;
        if (await isAdNetworkBlocked()) {
          if (resolved) return;
          console.warn("CMP script blocked or failed to load - using failover values");
          resolveOnce({ analytics: false, ads: false, reason: "cmp-blocked" });
          return;
        }
        if (resolved) return;
        console.info("Google consent modal did not load - using fallback consent banner");
        const result = await requestFallbackConsent();
        resolveOnce(result);
      }, CONSENT_WAIT_MS);
    },
  });
};

export default CMP;
